// @flow
import fetchRequest from 'app/packages/api/fetchRequest';
import logStats from 'app/common/lib/logStats';
import xhrRequest from 'app/packages/api/xhrRequest';
import { keepaliveIsSupported } from 'app/packages/api/keepaliveDetector';
import type { Config, AjaxHttpResponse } from 'app/packages/api/types';

type PlatformConfig = {|
  csrf: {
    isRequired: (method: string, url: string) => boolean,
    errorIfMissing: boolean,
    getToken: () => ?string,
  },
  getAppVersion: () => string,
  appendQuery: (url: string, params: Object) => string,
  buildQuery: (obj: Object) => string,
  // To avoid possible missing activations during page unload, we always activate both experiments.
  experimentGroups: {
    async: string,
    sync: string,
  },
  statsLogTags: { browser: string, platform: 'mobile' | 'desktop' },
|};

export type Options = $Shape<Config>;

// TODO(jackhsu): remove me after keepAlive investigation is complete.
// Since there is no extra privilege for being a tester, no actual authentication is needed.
// This is a quick & dirty hack for unauth experiment override for users who are in external IPs.
export const userIsGoogleKeepaliveTester = () =>
  ((window && window.location && window.location.search) || '')
    .substr(1)
    .split('&')
    .includes('google_keepalive_tester=1');

export default function ajax(
  options: Options,
  { csrf, getAppVersion, appendQuery, buildQuery, experimentGroups, statsLogTags }: PlatformConfig,
): {| promise: Promise<AjaxHttpResponse>, abort: () => void |} {
  const config = {
    async: true,
    cache: false,
    data: undefined,
    headers: {},
    query: {},
    timeout: 0,
    type: 'GET',
    ...options,
  };

  let useFetch = false;

  if (config.async) {
    const group = userIsGoogleKeepaliveTester() ? 'employees' : experimentGroups.async;
    useFetch = /^(employees|enabled)/.test(group);
    logStats.increment('ajax_calls_async', 1, {
      ...statsLogTags,
      useFetch,
      group,
    });
  } else {
    // sync is preferred
    const group = userIsGoogleKeepaliveTester() ? 'enabled_employees' : experimentGroups.sync;
    useFetch = group.startsWith('enabled') && keepaliveIsSupported(group === 'enabled_safari_13');
    if (useFetch) {
      config.headers['X-Fetch-Keepalive'] = 'true';
    }
    if (group) {
      config.headers['X-Fetch-Experiment-Group'] = group;
    }

    logStats.increment('ajax_calls_prefer_sync', 1, {
      ...statsLogTags,
      useFetch,
      group,
    });
  }

  let { url, data } = config;
  const method = config.type;

  // Manage query params and form data
  if (data && typeof data === 'object') {
    if (method === 'GET') {
      url = appendQuery(url, data);
      data = undefined;
    } else if (!(data instanceof FormData)) {
      data = buildQuery(data);
    }
  }

  // Add cache busting query param
  if (config.cache === false && method === 'GET') {
    url = appendQuery(url, { _: Date.now() });
  }

  const csrfTokenIsRequired = csrf.isRequired(method, url);

  if (csrf.errorIfMissing && csrfTokenIsRequired && !csrf.getToken()) {
    // if the request needs a csrf token and we don't have one
    // error and early return
    // This is likely because the user has cookies disabled
    return {
      promise: Promise.reject({ type: 'csrferror', message: 'No csrf token found' }),
      abort: () => {},
    };
  }

  const request = useFetch ? fetchRequest : xhrRequest;

  return request({
    ...config,
    url,
    data,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json, text/javascript, */*, q=0.01',
      'X-APP-VERSION': getAppVersion(),
      ...(method !== 'GET' && data && !(data instanceof FormData)
        ? { 'Content-Type': 'application/x-www-form-urlencoded' }
        : {}),
      ...(csrfTokenIsRequired ? { 'X-CSRFToken': csrf.getToken() || '' } : {}),
      ...config.headers,
    },
  });
}
